/**
 * Utilidades para comparar recursos extraídos con recursos existentes
 * en el flujo de auto-generación desde contexto.
 */

import type { ExtractedResource } from './AutoGenerateButton';

export interface ExistingResourcePreview {
  id: string;
  name: string;
  description: string | null;
  capacity: number;
  resourceTypeId: string;
  customAttributes: Record<string, string> | null;
}

export type AttributeStatus = 'new' | 'same' | 'different' | 'orphan';

export interface AttributeRow {
  key: string;
  proposedValue: string;
  existingValue?: string;
  status: AttributeStatus;
  /** Solo para atributos huérfanos (existen en BD pero no en el contexto). */
  markForDeletion: boolean;
}

export interface EditableResource {
  mode: 'create' | 'update';
  existingResourceId?: string;
  name: string;
  description: string;
  capacity: number;
  suggestedTypeName: string;
  selectedTypeId: string;
  existingDescription?: string | null;
  existingCapacity?: number;
  attributes: AttributeRow[];
}

export const NEW_TYPE_VALUE = '__new__';

export function normalizeResourceName(name: string): string {
  return name.trim().toLowerCase();
}

export function findExistingByName(
  name: string,
  existing: ExistingResourcePreview[]
): ExistingResourcePreview | undefined {
  const key = normalizeResourceName(name);
  return existing.find((r) => normalizeResourceName(r.name) === key);
}

function normalizeAttrMap(attrs: Record<string, string> | null | undefined): Record<string, string> {
  if (!attrs) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(attrs)) {
    const key = k.trim();
    if (!key) continue;
    out[key] = String(v).trim();
  }
  return out;
}

export function buildAttributeRows(
  existing: Record<string, string> | null | undefined,
  proposed: Record<string, string>
): AttributeRow[] {
  const prior = normalizeAttrMap(existing);
  const next = normalizeAttrMap(proposed);
  const rows: AttributeRow[] = [];
  const seen = new Set<string>();

  for (const [key, proposedValue] of Object.entries(next)) {
    seen.add(key);
    const existingValue = prior[key];
    if (existingValue === undefined) {
      rows.push({ key, proposedValue, status: 'new', markForDeletion: false });
    } else if (existingValue === proposedValue) {
      rows.push({ key, proposedValue, existingValue, status: 'same', markForDeletion: false });
    } else {
      rows.push({ key, proposedValue, existingValue, status: 'different', markForDeletion: false });
    }
  }

  for (const [key, existingValue] of Object.entries(prior)) {
    if (seen.has(key)) continue;
    rows.push({
      key,
      proposedValue: existingValue,
      existingValue,
      status: 'orphan',
      markForDeletion: false,
    });
  }

  return rows.sort((a, b) => a.key.localeCompare(b.key, 'es'));
}

export function findMatchingTypeId(
  suggestedName: string,
  types: { id: string; name: string }[]
): string {
  const match = types.find((t) => t.name.toLowerCase() === suggestedName.toLowerCase());
  return match ? match.id : NEW_TYPE_VALUE;
}

export function buildEditableResources(
  extracted: ExtractedResource[],
  existingResources: ExistingResourcePreview[],
  resourceTypes: { id: string; name: string }[]
): EditableResource[] {
  return extracted.map((r) => {
    const match = findExistingByName(r.name, existingResources);
    if (match) {
      return {
        mode: 'update' as const,
        existingResourceId: match.id,
        name: r.name,
        description: r.description ?? '',
        capacity: r.capacity,
        suggestedTypeName: r.suggestedTypeName,
        selectedTypeId: match.resourceTypeId,
        existingDescription: match.description,
        existingCapacity: match.capacity,
        attributes: buildAttributeRows(match.customAttributes, r.customAttributes ?? {}),
      };
    }
    return {
      mode: 'create' as const,
      name: r.name,
      description: r.description ?? '',
      capacity: r.capacity,
      suggestedTypeName: r.suggestedTypeName,
      selectedTypeId: findMatchingTypeId(r.suggestedTypeName, resourceTypes),
      attributes: buildAttributeRows(null, r.customAttributes ?? {}),
    };
  });
}

export function attributesToApply(rows: AttributeRow[]): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const row of rows) {
    if (row.status === 'orphan') continue;
    const key = row.key.trim();
    if (!key) continue;
    const proposed = row.proposedValue.trim();
    const existing = (row.existingValue ?? '').trim();
    if (row.status === 'same' && proposed === existing) continue;
    out[key] = proposed;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function orphanKeysToRemove(rows: AttributeRow[]): string[] {
  return rows
    .filter((r) => r.status === 'orphan' && r.markForDeletion)
    .map((r) => r.key.trim())
    .filter(Boolean);
}

export function scalarFieldChanged(
  mode: 'create' | 'update',
  current: string | number,
  existing: string | number | null | undefined
): boolean {
  if (mode === 'create') return false;
  if (existing === undefined || existing === null) {
    return String(current).trim() !== '';
  }
  if (typeof current === 'number' && typeof existing === 'number') {
    return current !== existing;
  }
  return String(current).trim() !== String(existing).trim();
}
